
import './ExpertCompliance.sass'
import { observer, inject } from 'mobx-react'
import React from 'react'
import SidePanel from '@root/Auth/Views/SidePanel'
import Logo from '@root/Auth/Views/SidePanel/logo.js'
import PropTypes from 'prop-types'

import { Formik, Form, Field } from 'formik'
import yup from 'yup'
import Button from '@components/Button'
import Input from '@components/Form/Input'
import Loading from '@components/spinner'
import EmailFound from './EmailFound'
import AnotherEmailFound from './AnotherEmailFound'
import ExpertComplianceAgreement from './ExpertComplianceAgreement'
import NoEmailFound from './NoEmailFound'
import TempResponsiveScreen from '@components/TempResponsiveScreen'
import ThankYou from '@components/TempResponsiveScreen/ThankYou'
import { Autocomplete } from '@components/Form/Autocomplete'
import { externGetAtheneumContacts } from '@atheneum/api'

const initialState = {
  complianceEmail: null,
  hasSearched: false,
  screen: null,
  currentLanguage: null,
  loading: false,
  error: false
}

class ExpertCompliance extends TempResponsiveScreen {
  static propTypes = {
    complianceStore: PropTypes.object,
  }

  constructor(props) {
    super(props)
    this.submitForm = this.submitForm.bind(this)
    this.setScreen = this.setScreen.bind(this)
    this.acceptComplianceAgreement = this.acceptComplianceAgreement.bind(this)
    this.declineComplianceAgreement = this.declineComplianceAgreement.bind(this)
    this.setThankYouScreen = this.setThankYouScreen.bind(this)
    this.resetState = this.resetState.bind(this)

    this.state = initialState
  }

  componentDidMount() {
    const path = window.location.pathname.split('/')
    const employeeID = path[path.length - 1]
    if (!isNaN(employeeID)) this.setState({ employeeID: +employeeID })
  }

  resetState() {
    this.setState({ ...initialState, employeeID: this.state.employeeID })
  }

  setScreen(screen) {
    this.setState({ screen })
  }

  setThankYouScreen() {
    this.setScreen('thankYou')
  }

  submitForm = (values, actions) => {
    this.setState({ loading: true })
    const { complianceStore } = this.props
    this.setState({ complianceEmail: values.email })
    try {
      complianceStore
        .expertComplianceEmailCheck(values)
        .catch(({ message }) => {
          this.setState({ error: message })
        }).finally(() => {
          this.setState({ hasSearched: true })
          actions.setSubmitting(false)
          this.setState({ loading: false })
        })
    } catch (error) {
      this.growlStore.showError(error)
    }
  }

  acceptComplianceAgreement(expertId) {
    const referrerID = this.getReferrer()
    this.props.complianceStore.setStaticComplianceSigned(expertId, referrerID)
  }

  declineComplianceAgreement(expertId) {
    this.props.complianceStore.setStaticComplianceDeclined(expertId, this.resetState)
  }

  createBasicProfile(values) {
    this.props.complianceStore.createBasicProfile(values, this.setThankYouScreen)
  }

  capitalizeName(name) {
    return name.charAt(0).toUpperCase() + name.slice(1)
  }

  getReferrer() {
    if (Boolean(this.state.employeeID)) {
      return this.state.employeeID
    } else {
      return this.state.selectedEmployeeID
    }
  }

  getEmployees(query) {
    if (!query || query.length < 3) return Promise.resolve([])

    return externGetAtheneumContacts(query)
  }

  itemToString = item => {
    const { employees } = this.props.complianceStore
    if (item && item.fullName) return item.fullName

    const employee = employees.find(({ id }) => Number(id) === Number(item))

    if (employee) return employee.fullName

    return ''
  }

  render() {
    const { complianceStore } = this.props
    const { user } = complianceStore
    const employeeAlreadyDefined = Boolean(this.state.employeeID)

    let complianceSigned = null
    let isProfileOk = null
    let expertEmails = []
    let userEmail = null
    let userName = null
    let userId = null

    if (user) {
      complianceSigned = !!user.expert.complianceSigned
      isProfileOk = !!user.expert.isProfileConfirmed
      expertEmails = user.address?.emails?.map(email => email.email)
      userEmail = user.email
      userName = user.fullName
      userId = user.id
    }

    if (this.state.loading) {
      return <Loading />
    }

    const pageTitle = 'Expert agreement'
    
    const screens = {
      complianceAgreement: (
        <ExpertComplianceAgreement
          isComplianceOk={complianceSigned}
          setCurrentLanguage={this.setCurrentLanguage}
          accept={() => this.acceptComplianceAgreement(userId)}
          decline={() => this.declineComplianceAgreement(userId)}
          create={values => this.createBasicProfile(values)}
        />
      ),
      thankYou: (
        <ThankYou
          pageTitle={pageTitle}
          accept={() => this.resetState()}
        />
      )
    }

    if (this.state.screen) {
      return screens[this.state.screen]
    }

    if (this.state.hasSearched) {
      if (this.state.complianceEmail === userEmail) {
        return (
          <EmailFound
            accept={() => complianceSigned && isProfileOk ? this.setScreen('thankYou') : this.setScreen('complianceAgreement')}
            pageTitle={pageTitle}
            email={userEmail}
            back={() => this.resetState()}
          />
        )
      }

      if (expertEmails.includes(this.state.complianceEmail)) {
        return (
          <AnotherEmailFound
            pageTitle={pageTitle}
            email={userEmail}
            accept={() => complianceSigned && isProfileOk ? this.setScreen('thankYou') : this.setScreen('complianceAgreement')}
            back={() => this.resetState()}
          />
        )
      }

      return (
        <NoEmailFound
          pageTitle={pageTitle}
          userName={userName}
          email={userEmail}
          back={() => this.resetState()}
        />
      )
    }

    return (
      <Formik
        initialValues={{
          email: ''
        }}
        validationSchema={getValidationSchema()}
        onSubmit={this.submitForm}
        render={form => (
          <div className='expert-compliance'>
            <SidePanel />
            <div className='form-expert-compliance'>
              <Logo className='expert-compliance-form-logo' />
              <h1 className='form-expert-compliance__title'>{pageTitle}</h1>
              <div className='form-inner'>
                <h3 className='form-expert-compliance__title form-expert-compliance__subtitle'>
                  Welcome
                </h3>
                <Form onSubmit={form.handleSubmit}>
                  { !employeeAlreadyDefined && 
                    <Field
                      name='referredBy'
                      label={'If known, please enter the name of your contact at Atheneum'}
                      itemToString={this.itemToString}
                      loadOptions={this.getEmployees}
                      onChange={(item) => {
                         this.setState({ selectedEmployeeID: item ? item.employeeID : null }) 
                      }}
                      component={Autocomplete}
                    />
                  }
                  
                  <Field
                    name='email'
                    label={'Please enter your email address'}
                    component={Input}
                  />
                  <div className='expert-compliance-form-button-section'>
                    <Button
                      className='expert-compliance-form-button'
                      red
                      submit
                      onClick={this.onSubmit}
                      disabled={form.isSubmitting}
                    >
                      Next
                    </Button>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        )}
      />
    )
  }
}

export default inject('complianceStore', 'growlStore')(observer(ExpertCompliance))

function getValidationSchema() {
  return yup.object().shape({
    email: yup
      .string()
      .email('Email format is invalid')
      .required('Email is required')
  })
}

